import React, { useState, useEffect } from 'react';
import { X, Save, Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { classService, schoolService } from '../../../services';
import { SchoolTeacher, SchoolTeacherCreateRequest } from '../types';
import toast from 'react-hot-toast';
import AccountFields from '../../../components/AccountFields';
import { getApiErrorMessage, getApiFieldErrors } from '../../../lib/api-error';

// Các field có dòng lỗi riêng dưới ô; lỗi field khác hiện ở banner
const SHOWN_ERROR_FIELDS = ['schoolId', 'email', 'user_name'];

interface CreateSchoolTeacherModalProps {
  isOpen: boolean;
  onClose: () => void;
  schoolId?: string;
  schoolName?: string;
  editingTeacher?: SchoolTeacher | null;
  // Ném lỗi thì modal giữ nguyên và hiện lỗi từ BE
  onSave: (data: SchoolTeacherCreateRequest) => void | Promise<void>;
  isLoading?: boolean;
}

const CreateSchoolTeacherModal: React.FC<CreateSchoolTeacherModalProps> = ({
  isOpen,
  onClose,
  schoolId,
  schoolName,
  editingTeacher,
  onSave,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<SchoolTeacherCreateRequest>({
    name: '',
    email: '',
    phone: '',
    user_name: '',
    schoolId: schoolId || '',
    schoolName: schoolName || '',
    code: '',
    unit: '',
    subjects: [],
    classification: '',
    monthlyQuota: 120,
    position: '',
    location: '',
    instructorType: 'fulltime',
    skills: [],
    certifications: [],
    hourlyRate: 500000,
    rating: 5.0,
    totalClasses: 0,
    metadata: {}
  });

  const [newSkill, setNewSkill] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [newCertification, setNewCertification] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');

  // Danh sách giáo viên đọc từ school_users, nên tạo mới ngoài trang trường phải chọn trường
  const needsSchoolPicker = !editingTeacher && !schoolId;
  const { data: schoolsResponse, isLoading: isLoadingSchools } = useQuery({
    queryKey: ['school-teacher-school-options'],
    queryFn: () => schoolService.getSchools({ page: 1, per_page: 1000 }),
    enabled: isOpen && needsSchoolPicker,
    staleTime: 5 * 60 * 1000,
  });
  const schoolOptions: Array<{ id: string; name: string; code?: string }> = schoolsResponse?.data || [];

  const { data: classesResponse, isLoading: isLoadingClasses } = useQuery({
    queryKey: ['school-teacher-classes', formData.schoolId],
    queryFn: () => classService.getClasses({ schoolId: formData.schoolId }),
    enabled: isOpen && !!editingTeacher && !!formData.schoolId,
    staleTime: 5 * 60 * 1000,
  });
  const availableClasses = classesResponse?.data || [];

  // Reset form when modal opens/closes or editing teacher changes
  useEffect(() => {
    if (isOpen) {
      if (editingTeacher) {
        setFormData({
          name: editingTeacher.name || '',
          email: editingTeacher.email || '',
          phone: editingTeacher.phone || '',
          schoolId: editingTeacher.schoolId || schoolId || '',
          schoolName: editingTeacher.schoolName || schoolName || '',
          code: editingTeacher.code || '',
          unit: editingTeacher.unit || '',
          subjects: editingTeacher.subjects || [],
          classification: editingTeacher.classification || '',
          monthlyQuota: editingTeacher.monthlyQuota,
          position: editingTeacher.position || '',
          location: editingTeacher.location || '',
          instructorType: editingTeacher.instructorType,
          skills: editingTeacher.skills || [],
          certifications: editingTeacher.certifications || [],
          hourlyRate: editingTeacher.hourlyRate,
          rating: editingTeacher.rating,
          totalClasses: editingTeacher.totalClasses,
          classId: editingTeacher.classId,
          metadata: editingTeacher.metadata || {}
        });
      } else {
        setFormData({
          name: '',
          email: '',
          phone: '',
          user_name: '',
          schoolId: schoolId || '',
          schoolName: schoolName || '',
          code: '',
          unit: '',
          subjects: [],
          classification: '',
          monthlyQuota: 120,
          position: '',
          location: '',
          instructorType: 'fulltime',
          skills: [],
          certifications: [],
          hourlyRate: 500000,
          rating: 5.0,
          totalClasses: 0,
          metadata: {}
        });
      }
      setNewSkill('');
      setNewSubject('');
      setNewCertification('');
      setErrors({});
      setFormError('');
    }
  }, [isOpen, editingTeacher, schoolId, schoolName]);

  const setField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || (!editingTeacher && !formData.email)) {
      toast.error('Vui lòng điền đầy đủ thông tin bắt buộc');
      return;
    }

    setErrors({});
    setFormError('');
    try {
      await onSave(formData);
    } catch (error) {
      setErrors(getApiFieldErrors(error));
      setFormError(getApiErrorMessage(error, 'Có lỗi xảy ra, vui lòng thử lại', SHOWN_ERROR_FIELDS));
    }
  };

  const addSkill = () => {
    if (newSkill.trim() && !formData.skills?.includes(newSkill.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...(prev.skills || []), newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const removeSkill = (skillToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills?.filter(skill => skill !== skillToRemove) || []
    }));
  };

  const addSubject = () => {
    if (newSubject.trim() && !formData.subjects?.includes(newSubject.trim())) {
      setFormData(prev => ({
        ...prev,
        subjects: [...(prev.subjects || []), newSubject.trim()]
      }));
      setNewSubject('');
    }
  };

  const removeSubject = (subjectToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects?.filter(subject => subject !== subjectToRemove) || []
    }));
  };

  const addCertification = () => {
    if (newCertification.trim() && !formData.certifications?.includes(newCertification.trim())) {
      setFormData(prev => ({
        ...prev,
        certifications: [...(prev.certifications || []), newCertification.trim()]
      }));
      setNewCertification('');
    }
  };

  const removeCertification = (certToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications?.filter(cert => cert !== certToRemove) || []
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {editingTeacher ? 'Chỉnh sửa giáo viên' : 'Thêm giáo viên mới'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{formError}</div>
            )}
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Thông tin cơ bản</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {needsSchoolPicker && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Trường <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.schoolId}
                      onChange={(e) => {
                        const school = schoolOptions.find(s => s.id === e.target.value);
                        setField('schoolId', e.target.value);
                        setFormData(prev => ({ ...prev, schoolName: school?.name || '' }));
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.schoolId ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                      disabled={isLoadingSchools}
                    >
                      <option value="">{isLoadingSchools ? 'Đang tải danh sách trường...' : '-- Chọn trường --'}</option>
                      {schoolOptions.map((school) => (
                        <option key={school.id} value={school.id}>
                          {school.name}{school.code ? ` (${school.code})` : ''}
                        </option>
                      ))}
                    </select>
                    {errors.schoolId && <p className="text-red-500 text-sm mt-1">{errors.schoolId}</p>}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Họ tên <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập họ tên"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email {!editingTeacher && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="email@example.com"
                    required={!editingTeacher}
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Số điện thoại
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0123456789"
                  />
                </div>
                {!editingTeacher && (
                  <AccountFields
                    userName={formData.user_name || ''}
                    onChange={setField}
                    errors={errors}
                    disabled={isLoading}
                  />
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mã giáo viên</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="GV001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vị trí/Chức danh</label>
                  <input
                    type="text"
                    value={formData.position}
                    onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Giảng viên chính"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Đơn vị</label>
                  <select
                    value={formData.unit}
                    onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Chọn đơn vị</option>
                    <option value="Chi nhánh Hồ Chí Minh">Chi nhánh Hồ Chí Minh</option>
                    <option value="Chi nhánh Hà Nội">Chi nhánh Hà Nội</option>
                    <option value="Chi nhánh Đà Nẵng">Chi nhánh Đà Nẵng</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Địa điểm</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Hồ Chí Minh"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phân loại giảng viên</label>
                  <select
                    value={formData.instructorType ?? ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, instructorType: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="fulltime">Giảng viên</option>
                    <option value="parttime">Kiêm nhiệm</option>
                    <option value="guest">Thỉnh giảng</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Teaching Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Thông tin giảng dạy</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {editingTeacher && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lớp học</label>
                    <select
                      value={formData.classId || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, classId: e.target.value || null }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={isLoadingClasses}
                    >
                      <option value="">Chưa phân lớp</option>
                      {availableClasses.map((classItem) => (
                        <option key={classItem.id} value={classItem.id}>
                          {classItem.name} - {classItem.schoolYear}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quota dạy/tháng (giờ)</label>
                  <input
                    type="number"
                    value={formData.monthlyQuota ?? ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      monthlyQuota: e.target.value === '' ? undefined : Number.parseInt(e.target.value, 10),
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    max="200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mức giá/giờ (VNĐ)</label>
                  <input
                    type="number"
                    value={formData.hourlyRate ?? ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      hourlyRate: e.target.value === '' ? undefined : Number.parseInt(e.target.value, 10),
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    step="50000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phân loại</label>
                  <select
                    value={formData.classification}
                    onChange={(e) => setFormData(prev => ({ ...prev, classification: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Chọn phân loại</option>
                    <option value="senior">Giảng viên cao cấp</option>
                    <option value="associate">Giảng viên</option>
                    <option value="assistant">Trợ giảng</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Đánh giá</label>
                  <input
                    type="number"
                    value={formData.rating ?? ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      rating: e.target.value === '' ? undefined : Number.parseFloat(e.target.value),
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    max="5"
                    step="0.1"
                  />
                </div>
              </div>
            </div>

            {/* Subjects */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Môn dạy</h3>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Thêm môn dạy..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubject())}
                />
                <button
                  type="button"
                  onClick={addSubject}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.subjects?.map((subject) => (
                  <span key={subject} className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                    {subject}
                    <button
                      type="button"
                      onClick={() => removeSubject(subject)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Skills */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Kỹ năng</h3>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Thêm kỹ năng..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                />
                <button
                  type="button"
                  onClick={addSkill}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.skills?.map((skill) => (
                  <span key={skill} className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                    {skill}
                    <button
                      type="button"
                      onClick={() => removeSkill(skill)}
                      className="ml-2 text-green-600 hover:text-green-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Certifications */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Chứng chỉ</h3>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newCertification}
                  onChange={(e) => setNewCertification(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Thêm chứng chỉ..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
                />
                <button
                  type="button"
                  onClick={addCertification}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.certifications?.map((cert) => (
                  <span key={cert} className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">
                    {cert}
                    <button
                      type="button"
                      onClick={() => removeCertification(cert)}
                      className="ml-2 text-purple-600 hover:text-purple-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {isLoading ? 'Đang lưu...' : (editingTeacher ? 'Cập nhật' : 'Thêm mới')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSchoolTeacherModal;
